import Button from '@shared/components/Button/Button';
import cx from 'classnames';
import { useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import Spinner from 'components/Spinner/Spinner';
import SettingsCard from 'modules/SettingsModal/SettingsCard/SettingsCard';

export const AVATAR_ACCEPT_INPUT_FORMATS = 'image/jpeg,image/jpg,image/webp,image/gif,image/png';

export const AVATAR_ACCEPT_DROPZONE_FORMATS = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif'],
  'image/png': ['.png'],
};

interface AvatarSettingProps {
  title: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onClear: () => void;
  isLoading: boolean;
  fallbackAvatar: React.ReactNode;
  avatarUrl?: string;
}

const AvatarSetting = ({
  title,
  avatarUrl,
  fallbackAvatar: FallbackAvatar,
  onChange,
  onClear,
  isLoading,
}: AvatarSettingProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onDrop = async (acceptedFiles: File[]) => {
    if (!acceptedFiles[0]) return;
    const [file] = acceptedFiles;
    const event = { target: { files: [file] } };
    await onChange(event as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const { getRootProps, isDragActive } = useDropzone({
    onDrop,
    accept: AVATAR_ACCEPT_DROPZONE_FORMATS,
    maxFiles: 1,
    noClick: true,
  });

  return (
    <SettingsCard.Content>
      <div className="flex flex-col space-y-4">
        <div className="flex flex-col space-y-1">
          <SettingsCard.Content.Title>{title}</SettingsCard.Content.Title>
          <SettingsCard.Content.Description>{t('profile_picture_explanation')}</SettingsCard.Content.Description>
        </div>
        <div className="flex">
          <div
            {...getRootProps()}
            className={cx(
              'relative w-20 h-20 rounded-2xl transition-all duration-200 border-2 border-dashed border-transparent box-content',
              { '!border-primary-50': isDragActive }
            )}
          >
            {isLoading && <Spinner className="absolute m-auto inset-0 z-10" />}
            {avatarUrl ? (
              <img
                className="absolute inset-0 w-full h-full object-cover rounded-2xl shadow-sm shadow-gray-40 cursor-default select-none drag-none"
                src={avatarUrl}
                alt="avatar"
                draggable={false}
              />
            ) : (
              FallbackAvatar
            )}
          </div>
          <div className="flex flex-col items-start justify-between ml-3 sm:ml-0 sm:flex-row sm:items-end">
            <input
              type="file"
              accept={AVATAR_ACCEPT_INPUT_FORMATS}
              ref={fileInputRef}
              className="hidden"
              key={avatarUrl}
              onChange={onChange}
            />
            <div className="sm:flex">
              <Button
                variant="secondaryTertiary"
                size="sm"
                className="h-10 sm:mx-3 px-4 p-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
              >
                {t('edit_image')}
              </Button>
              <Button
                variant="secondaryTertiary"
                size="sm"
                className={cx('h-10 p-2 mt-1 px-4 sm:mt-0', {
                  '!text-alert-50': avatarUrl && !isLoading,
                })}
                onClick={onClear}
                disabled={!avatarUrl || isLoading}
              >
                {t('remove')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </SettingsCard.Content>
  );
};

export default AvatarSetting;
